import { CreateEditDto } from '@app/modules/video-editions/types';
import { useWebAppCreateEditVideoEditorEdits } from '@mi-front/api-v2';
import { useCallback } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useRedirectToVideoEditorEdit } from './useVideoEditorEditUrl';

const autoEditNameMessage = defineMessage({
  id: 'videoEditions.autoEditName',
  defaultMessage: 'Edit {n}',
});

export interface UseVideoEditionQuickCreateParams {
  /** Builds the API body for creating an edit; `name` is the localized auto-generated label. */
  buildCreatePayload: (name: string) => CreateEditDto;
  /** Optional cache refresh after create succeeds and before redirecting to the editor. */
  onSuccess?: () => Promise<void> | void;
}

/**
 * Creates a video editor edit with a localized auto-generated name, optionally refreshes queries,
 * then redirects to the video editor on success.
 */
export function useVideoEditionQuickCreate({
  buildCreatePayload,
  onSuccess,
}: UseVideoEditionQuickCreateParams) {
  const { $t } = useIntl();
  const redirectToVideoEditorEdit = useRedirectToVideoEditorEdit();

  const { mutate, isPending } = useWebAppCreateEditVideoEditorEdits({
    mutation: {
      onSuccess: async (created) => {
        await onSuccess?.();
        redirectToVideoEditorEdit(created.id);
      },
    },
  });

  const createWithAutoName = useCallback(
    (currentEditCount: number) => {
      const n = currentEditCount + 1;
      const name = $t(autoEditNameMessage, { n });
      mutate({
        data: buildCreatePayload(name),
      });
    },
    [$t, mutate, buildCreatePayload],
  );

  return {
    createWithAutoName,
    isCreating: isPending,
  };
}
